import { type ContactDetailsInitialValues } from "@lib/utils/checkoutHelper";
import { getContactDetailsValidationSchema } from "@lib/validators/checkout/contactDetailsValidationSchema";
import { Box } from "@ecco/ui/box";
import { type FC, useEffect } from "react";
import StepForm from "../StepForm";
import StepSubmitButton from "../StepSubmitButton";
import {
    CONTACT_DETAILS_STEP,
    CONTACT_SUBMIT_BUTTON_TEST_ID,
    CONTACT_POLICY_CHECKBOX_TEST_ID,
} from "@e2e/helpers/checkout/components/contact-details/constants";
import { useContactFormMicrocopy } from "./ContactDetails.microcopy";
import type { IContactDetailsStepFormProps, IFormDetailsProps } from "./types";
import { PhoneNumberField, NewsletterConsentField } from "./form";
import { ContactDetailsStepSummary } from "./ContactDetailsStepSummary";
import { useCheckoutErrorState } from "@features/checkout/utils/useCheckoutErrorState";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { fieldStateHelper } from "@lib/utils/form/fieldStateHelper/fieldStateHelper";
import { CheckboxField } from "@components/ui/form/CheckboxField/CheckboxField";
import EmailField from "./form/EmailField";
import { RUM_ACTION_NAME } from "@lib/monitoring/rumActionNames";

const FormDetails: FC<IFormDetailsProps> = ({
    handleSubmit,
    initialValues,
    validationErrorField,
    policyAgreement,
    newsletterConsentTitle,
    newsletterConsentPolicy,
}) => {
    const { headers, headerErrors } = useContactFormMicrocopy();
    const methods = useForm({
        resolver: yupResolver(
            getContactDetailsValidationSchema(headerErrors, {
                email: headers.email,
                phone: headers.phone,
            })
        ),
        defaultValues: initialValues,
        shouldUnregister: true,
        mode: "onTouched",
        reValidateMode: "onChange",
    });

    const onSubmit = (values: ContactDetailsInitialValues) => {
        handleSubmit(values);
    };
    const { register, formState } = methods;
    const { errors, touchedFields } = formState;

    useEffect(() => {
        if (!validationErrorField) {
            return;
        }

        methods.setValue(validationErrorField, initialValues[validationErrorField], {
            shouldTouch: true,
            shouldValidate: true,
        });
        methods.setFocus(validationErrorField);
    }, [initialValues, methods, validationErrorField]);

    const { getFieldStateProps } = fieldStateHelper({
        errors,
        touched: touchedFields,
        type: "hook-form",
        isSubmitted: formState.isSubmitted,
    });

    const { isInvalid: isPolicyInvalid, ...restPolicyFieldStateProps } =
        getFieldStateProps("policy");

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} data-testid={CONTACT_DETAILS_STEP}>
                <Box w={["100%", "100%", "100%", "60%"]}>
                    <EmailField />

                    <PhoneNumberField
                        countryCodeLabel={headers.country}
                        phoneNumberLabel={headers.phone}
                        helpText={headers.reasonForPhone}
                    />

                    <CheckboxField
                        name="policy"
                        label={policyAgreement}
                        {...register("policy")}
                        {...restPolicyFieldStateProps}
                        isInvalid={isPolicyInvalid}
                        aria-invalid={isPolicyInvalid}
                        testId={CONTACT_POLICY_CHECKBOX_TEST_ID}
                    />

                    {newsletterConsentTitle && newsletterConsentPolicy && (
                        <NewsletterConsentField
                            newsletterConsentTitle={newsletterConsentTitle}
                            newsletterConsentPolicy={newsletterConsentPolicy}
                        />
                    )}

                    <StepSubmitButton
                        onClick={methods.handleSubmit(onSubmit)}
                        testId={CONTACT_SUBMIT_BUTTON_TEST_ID}
                        headerText={headers.continueToDelivery}
                        data-dd-action-name={RUM_ACTION_NAME.CHECKOUT_CONTACT_SUBMIT}
                    />
                </Box>
            </form>
        </FormProvider>
    );
};

const ContactDetailsStepForm: FC<IContactDetailsStepFormProps> = ({
    onSubmit,
    initialValues,
    validationErrorField,
    title,
    policyAgreement,
    newsletterConsentTitle,
    newsletterConsentPolicy,
    isExpandedId,
    handleExpanded,
    stepId,
}) => {
    const { errors } = useCheckoutErrorState();
    const errorMessage = errors[stepId];
    return (
        <StepForm
            title={title}
            values={initialValues}
            stepId={stepId}
            isExpandedId={isExpandedId}
            handleExpanded={handleExpanded}
            errorMessage={errorMessage}
            summary={<ContactDetailsStepSummary values={initialValues} />}
        >
            <FormDetails
                handleSubmit={onSubmit}
                initialValues={initialValues}
                validationErrorField={validationErrorField}
                policyAgreement={policyAgreement}
                newsletterConsentTitle={newsletterConsentTitle}
                newsletterConsentPolicy={newsletterConsentPolicy}
            />
        </StepForm>
    );
};

export default ContactDetailsStepForm;
